import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Building2, Plus, X } from 'lucide-react';
import { useToast } from '@/components/common/Toast';
import layerAdmin, { CompanySettings } from '@/services/layerAdmin.service';
import ums, { FEATURES, geonexoraSubscription, PRODUCT, slugOf, UmsTenant } from '@/services/ums.service';
import { usesUms } from '@/auth/umsSession';
import CompanyUsers from '@/components/access/CompanyUsers';
import StaffMembership from '@/components/access/StaffMembership';
import LayerGrantPicker, { CompanySettingsFields, Grants } from '@/components/access/LayerGrantPicker';

/**
 * Companies: opening one, its seats and features (GeoNexora's own UMS), its people (UMS), and which of our
 * layers it sees, without which columns, with how many analyses (GeoNexora's taxonomy, geonexora-server).
 * The layer choices are GeoNexora's alone: nothing of them goes to UMS or to Veramenta.
 */

const PLATFORM = '00000000-0000-0000-0000-000000000001';
const input = 'w-full px-3 py-2 border border-gn-light rounded bg-gn-surface text-gn-primary';
const button = 'px-3 py-2 border border-gn-light rounded hover:bg-gn-tertiary disabled:opacity-50 text-sm';
const primary = 'px-4 py-2 bg-gn-blue text-gn-inverse rounded text-sm disabled:opacity-50';

interface Row {
  id: string;
  name: string;
  /** In UMS: ACTIVE or SUSPENDED; null for a company only GeoNexora knows (from before UMS). */
  status: string | null;
  seats?: number | null;
  layers?: number;
  tenant?: UmsTenant;
}

type Tab = 'data' | 'subscription' | 'users';

const CompanyManagement: React.FC = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('data');
  const [creating, setCreating] = useState(false);

  const { data: umsOn = false } = useQuery({ queryKey: ['usesUms'], queryFn: usesUms });
  const { data: companies = [] } = useQuery({ queryKey: ['adminCompanies'], queryFn: () => layerAdmin.companies() });
  const tenants = useQuery({ queryKey: ['umsTenants'], queryFn: () => ums.tenants(), enabled: umsOn, retry: false });
  const { data: layers = [] } = useQuery({ queryKey: ['adminLayers'], queryFn: () => layerAdmin.layers() });

  const rows: Row[] = useMemo(() => {
    const byId = new Map<string, Row>();
    for (const tn of tenants.data ?? []) {
      if (tn.id === PLATFORM) continue;
      byId.set(tn.id, { id: tn.id, name: tn.displayName, status: tn.status, seats: geonexoraSubscription(tn)?.seatLimit, tenant: tn });
    }
    for (const c of companies) {
      const r = byId.get(c.tenant);
      if (r) r.layers = c.layers;
      else byId.set(c.tenant, { id: c.tenant, name: c.name, status: null, layers: c.layers });
    }
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [tenants.data, companies]);

  const current = rows.find((r) => r.id === selected) ?? null;
  const copyFrom = companies.map((c) => ({ tenant: c.tenant, name: c.name }));

  return (
    <div className="flex gap-6">
      <div className="w-72 shrink-0">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-xl font-bold">{t('companies.title')}</h2>
          {umsOn && (
            <button type="button" className="inline-flex items-center gap-1 px-3 py-1.5 bg-gn-blue text-gn-inverse rounded text-sm"
                    onClick={() => setCreating(true)}>
              <Plus size={16} /> {t('companies.add')}
            </button>
          )}
        </div>
        <p className="text-sm text-gn-muted mb-4">{t('companies.hint')}</p>
        {tenants.isError && <p className="text-xs text-gn-danger mb-2">{(tenants.error as Error).message}</p>}
        <ul className="space-y-1">
          {rows.map((c) => (
            <li key={c.id}>
              <button type="button" onClick={() => { setSelected(c.id); setTab('data'); }}
                      className={`w-full text-left px-3 py-2 rounded ${selected === c.id ? 'bg-gn-blue text-gn-inverse' : 'hover:bg-gn-surface'}`}>
                <div className="font-medium flex items-center gap-2">
                  {c.name}
                  {c.status === 'SUSPENDED' && <span className="text-xs px-1 rounded bg-gn-danger-light text-gn-danger">{t('companies.suspended')}</span>}
                </div>
                <div className="text-xs opacity-75">
                  {t('companies.layer_count', { count: c.layers ?? 0 })}
                  {c.status && ` · ${c.seats == null ? t('companies.seats_unlimited') : t('companies.seats', { count: c.seats })}`}
                  {!c.status && umsOn && ` · ${t('companies.not_in_ums')}`}
                </div>
              </button>
            </li>
          ))}
          {rows.length === 0 && <li className="text-sm text-gn-muted">{t('companies.none')}</li>}
        </ul>
      </div>

      {current && (
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-4">
            <Building2 size={20} />
            <h3 className="text-lg font-bold">{current.name}</h3>
          </div>
          {umsOn && current.tenant && current.status === 'ACTIVE' && (
            <StaffMembership key={current.id} tenantId={current.id} companyName={current.name} />
          )}
          <div className="flex gap-1 border-b border-gn-light mb-4">
            {(['data', 'subscription', 'users'] as Tab[])
              .filter((k) => k === 'data' || current.tenant)
              .map((k) => (
                <button key={k} type="button" onClick={() => setTab(k)}
                        className={`px-4 py-2 text-sm -mb-px border-b-2 ${tab === k ? 'border-gn-blue text-gn-primary font-medium' : 'border-transparent text-gn-muted'}`}>
                  {t(`companies.tab_${k}`)}
                </button>
              ))}
          </div>
          {tab === 'data' && <CompanyData id={current.id} name={current.name} layers={layers} copyFrom={copyFrom.filter((c) => c.tenant !== current.id)} />}
          {tab === 'subscription' && current.tenant && <CompanySubscription tenant={current.tenant} />}
          {tab === 'users' && current.tenant && <CompanyUsers tenantId={current.id} seatLimit={current.seats} />}
        </div>
      )}

      {creating && (
        <NewCompany layers={layers} copyFrom={copyFrom}
                    onClose={() => setCreating(false)}
                    onCreated={(id) => {
                      setCreating(false);
                      queryClient.invalidateQueries({ queryKey: ['umsTenants'] });
                      queryClient.invalidateQueries({ queryKey: ['adminCompanies'] });
                      queryClient.invalidateQueries({ queryKey: ['adminLayers'] });
                      setSelected(id);
                      setTab('users');
                      addToast(t('companies.created'), 'success');
                    }} />
      )}
    </div>
  );
};

/** The company's layers, hidden columns, credits and map: GeoNexora's own. */
const CompanyData: React.FC<{ id: string; name: string; layers: Parameters<typeof LayerGrantPicker>[0]['layers']; copyFrom: { tenant: string; name: string }[] }> =
  ({ id, name, layers, copyFrom }) => {
    const { t } = useTranslation();
    const { addToast } = useToast();
    const queryClient = useQueryClient();
    const [settings, setSettings] = useState<Partial<CompanySettings>>({});
    const [grants, setGrants] = useState<Grants>({});
    const company = useQuery({ queryKey: ['adminCompany', id], queryFn: () => layerAdmin.company(id), retry: false });

    useEffect(() => {
      if (company.data) {
        setSettings(company.data);
        setGrants({ ...(company.data.grants ?? {}) });
      } else if (company.isError) {
        // Opened in UMS, not yet here: everything starts empty and is made on save.
        setSettings({ driveCredits: -1, walkCredits: -1, motorCredits: -1 });
        setGrants({});
      }
    }, [company.data, company.isError]);

    const save = useMutation({
      mutationFn: async () => {
        await layerAdmin.saveCompany(id, { ...settings, name });
        return layerAdmin.grant(id, grants);
      },
      onSuccess: (c) => {
        queryClient.setQueryData(['adminCompany', id], c);
        queryClient.invalidateQueries({ queryKey: ['adminCompanies'] });
        queryClient.invalidateQueries({ queryKey: ['adminLayers'] });
        addToast(t('companies.saved'), 'success');
      },
      onError: () => addToast(t('companies.save_failed'), 'error'),
    });

    return (
      <div>
        <div className="mb-4"><CompanySettingsFields settings={settings} onChange={setSettings} tenant={id} /></div>
        <div className="flex justify-end mb-3">
          <button type="button" onClick={() => save.mutate()} disabled={save.isPending} className={primary}>{t('buttons.save')}</button>
        </div>
        <LayerGrantPicker layers={layers} grants={grants} onChange={setGrants} copyFrom={copyFrom} />
      </div>
    );
  };

/** Seats, features and whether the company may sign in: UMS. */
const CompanySubscription: React.FC<{ tenant: UmsTenant }> = ({ tenant }) => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const sub = geonexoraSubscription(tenant);
  const [seats, setSeats] = useState<number | null>(sub?.seatLimit ?? null);
  const [features, setFeatures] = useState<string[]>(sub?.features ?? [...FEATURES]);
  const [validUntil, setValidUntil] = useState<string>(sub?.validUntil ? sub.validUntil.substring(0, 10) : '');

  useEffect(() => {
    setSeats(sub?.seatLimit ?? null);
    setFeatures(sub?.features ?? [...FEATURES]);
    setValidUntil(sub?.validUntil ? sub.validUntil.substring(0, 10) : '');
  }, [tenant.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['umsTenants'] });
  const failed = (e: unknown) => addToast(e instanceof Error ? e.message : String(e), 'error');
  const save = useMutation({
    mutationFn: () => ums.setSubscription(tenant.id, {
      seatLimit: seats, features, plan: sub?.plan ?? null,
      validUntil: validUntil ? new Date(`${validUntil}T23:59:59Z`).toISOString() : null,
    }),
    onSuccess: () => { refresh(); addToast(t('companies.saved'), 'success'); },
    onError: failed,
  });
  const status = useMutation({
    mutationFn: () => (tenant.status === 'SUSPENDED' ? ums.reactivateTenant(tenant.id) : ums.suspendTenant(tenant.id)),
    onSuccess: () => { refresh(); addToast(t('companies.saved'), 'success'); },
    onError: failed,
  });

  return (
    <div className="max-w-xl space-y-4">
      <SeatsField seats={seats} onChange={setSeats} />
      <FeaturesField features={features} onChange={setFeatures} />
      <label className="block text-sm">{t('companies.valid_until')}
        <input type="date" className={input} value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
      </label>
      <div className="flex gap-2">
        <button type="button" className={primary} disabled={save.isPending} onClick={() => save.mutate()}>{t('buttons.save')}</button>
      </div>
      <div className="border-t border-gn-light pt-4">
        <p className="text-sm mb-2">{tenant.status === 'SUSPENDED' ? t('companies.suspended_hint') : t('companies.suspend_hint')}</p>
        <button type="button" className={button} disabled={status.isPending}
                onClick={() => { if (tenant.status === 'SUSPENDED' || window.confirm(t('companies.suspend_confirm', { name: tenant.displayName }))) status.mutate(); }}>
          {tenant.status === 'SUSPENDED' ? t('companies.reactivate') : t('companies.suspend')}
        </button>
      </div>
    </div>
  );
};

const SeatsField: React.FC<{ seats: number | null; onChange: (n: number | null) => void }> = ({ seats, onChange }) => {
  const { t } = useTranslation();
  return (
    <div>
      <span className="text-sm">{t('companies.seat_limit')}</span>
      <div className="flex gap-2 items-center">
        <input type="number" min={1} className={input} disabled={seats == null} value={seats ?? ''}
               onChange={(e) => onChange(Math.max(1, Number(e.target.value)))} />
        <label className="inline-flex items-center gap-1 text-sm whitespace-nowrap">
          <input type="checkbox" checked={seats == null} onChange={(e) => onChange(e.target.checked ? null : 5)} />
          {t('companies.unlimited')}
        </label>
      </div>
      <p className="text-xs text-gn-muted mt-1">{t('companies.seat_hint')}</p>
    </div>
  );
};

const FeaturesField: React.FC<{ features: string[]; onChange: (f: string[]) => void }> = ({ features, onChange }) => {
  const { t } = useTranslation();
  return (
    <div>
      <span className="text-sm">{t('companies.features')}</span>
      <div className="grid grid-cols-2 gap-1 mt-1">
        {FEATURES.map((f) => (
          <label key={f} className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={features.includes(f)}
                   disabled={f === 'geonexora.core'}
                   onChange={(e) => onChange(e.target.checked ? [...features, f] : features.filter((x) => x !== f))} />
            {t(`companies.feature.${f.split('.')[1]}`)}
          </label>
        ))}
      </div>
    </div>
  );
};

/**
 * Opening a company, in three steps: the company in UMS (name, seats, features, first administrator), then
 * which of our layers it sees, then its credits and map - the last two in geonexora-server.
 */
const NewCompany: React.FC<{
  layers: Parameters<typeof LayerGrantPicker>[0]['layers'];
  copyFrom: { tenant: string; name: string }[];
  onClose: () => void;
  onCreated: (id: string) => void;
}> = ({ layers, copyFrom, onClose, onCreated }) => {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [seats, setSeats] = useState<number | null>(5);
  const [features, setFeatures] = useState<string[]>([...FEATURES]);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminName, setAdminName] = useState('');
  const [grants, setGrants] = useState<Grants>({});
  const [settings, setSettings] = useState<Partial<CompanySettings>>({ driveCredits: -1, walkCredits: -1, motorCredits: -1 });
  const [error, setError] = useState<string | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);

  const effectiveSlug = slugTouched ? slug : slugOf(name);
  const step1Ok = name.trim().length > 1 && /^[a-z0-9][a-z0-9-]{1,59}$/.test(effectiveSlug)
    && adminEmail.includes('@') && adminName.trim().length > 0;

  const create = useMutation({
    mutationFn: async () => {
      // Once made in UMS, a retry only finishes the GeoNexora part.
      let id = createdId;
      if (!id) {
        id = (await ums.createTenant({
          slug: effectiveSlug,
          displayName: name.trim(),
          seatLimit: seats,
          features,
          firstAdmin: { email: adminEmail.trim(), displayName: adminName.trim(), locale: (i18n.language || 'en').substring(0, 2) },
        })).id;
        setCreatedId(id);
        // UMS gives the first administrator its company-admin role (people of this company only). GeoNexora's
        // own administrator role - inside this company, and the seat it takes - is given here.
        const email = adminEmail.trim().toLowerCase();
        const [members, roles] = await Promise.all([ums.members(id), ums.roles(id)]);
        const admin = members.find((m) => (m.email ?? '').toLowerCase() === email);
        const gxAdmin = roles.find((r) => r.product === PRODUCT && r.key === 'admin' && r.template);
        if (admin && gxAdmin) await ums.assign(id, admin.principalId, gxAdmin.id);
      }
      await layerAdmin.saveCompany(id, { ...settings, name: name.trim() });
      await layerAdmin.grant(id, grants);
      return id;
    },
    onSuccess: (id) => onCreated(id),
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6">
      <div className="bg-gn-tertiary text-gn-primary rounded-lg shadow-xl w-full max-w-5xl max-h-full flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gn-light">
          <div>
            <h3 className="text-lg font-bold">{t('companies.new_title')}</h3>
            <p className="text-xs text-gn-muted">{t('companies.step', { step, of: 3 })} · {t(`companies.step_${step}`)}</p>
          </div>
          <button type="button" onClick={onClose} aria-label={t('buttons.cancel')}><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-auto px-6 py-4">
          {step === 1 && (
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-3">
                <label className="block text-sm">{t('companies.name')}
                  <input className={input} value={name} autoFocus disabled={!!createdId} onChange={(e) => setName(e.target.value)} />
                </label>
                <label className="block text-sm">{t('companies.slug')}
                  <input className={input} value={effectiveSlug} disabled={!!createdId}
                         onChange={(e) => { setSlugTouched(true); setSlug(e.target.value.toLowerCase()); }} />
                  <span className="text-xs text-gn-muted">{t('companies.slug_hint')}</span>
                </label>
                <SeatsField seats={seats} onChange={setSeats} />
                <FeaturesField features={features} onChange={setFeatures} />
              </div>
              <div className="space-y-3">
                <p className="text-sm font-medium">{t('companies.first_admin')}</p>
                <p className="text-xs text-gn-muted">{t('companies.first_admin_hint')}</p>
                <label className="block text-sm">{t('access.email')}
                  <input className={input} type="email" value={adminEmail} disabled={!!createdId} onChange={(e) => setAdminEmail(e.target.value)} />
                </label>
                <label className="block text-sm">{t('access.name')}
                  <input className={input} value={adminName} disabled={!!createdId} onChange={(e) => setAdminName(e.target.value)} />
                </label>
              </div>
            </div>
          )}
          {step === 2 && <LayerGrantPicker layers={layers} grants={grants} onChange={setGrants} copyFrom={copyFrom} />}
          {step === 3 && (
            <div>
              <CompanySettingsFields settings={settings} onChange={setSettings} />
              <div className="mt-6 text-sm space-y-1">
                <p><b>{name}</b> ({effectiveSlug})</p>
                <p>{seats == null ? t('companies.seats_unlimited') : t('companies.seats', { count: seats })} · {t('companies.layer_count', { count: Object.keys(grants).length })}</p>
                <p>{t('companies.first_admin')}: {adminName} &lt;{adminEmail}&gt;</p>
              </div>
            </div>
          )}
          {error && <p className="text-sm text-gn-danger mt-4">{error}</p>}
          {createdId && error && <p className="text-xs text-gn-muted mt-1">{t('companies.created_in_ums')}</p>}
        </div>

        <div className="flex justify-between px-6 py-4 border-t border-gn-light">
          <button type="button" className={button} disabled={step === 1} onClick={() => setStep(step - 1)}>{t('buttons.back')}</button>
          {step < 3 ? (
            <button type="button" className={primary} disabled={step === 1 && !step1Ok} onClick={() => { setError(null); setStep(step + 1); }}>
              {t('buttons.next')}
            </button>
          ) : (
            <button type="button" className={primary} disabled={create.isPending} onClick={() => { setError(null); create.mutate(); }}>
              {create.isPending ? '…' : t('companies.create')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyManagement;
